import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { feel } from './native-feel/index.js';
import { MoonIcon, SunIcon, ThemeAutoIcon } from './icons/index.js';

/**
 * Viewer theme control — ONE TRUE source (backlog A/F1, second half).
 *
 * Every app already had a complete dark palette. Only `apps/marketing` had any
 * way to REACH it; in `find`, `dashboard`, `agent-pwa` and `customer-pwa` the
 * only route to dark mode was changing the operating system setting. A palette
 * nobody can select is work that never ships.
 *
 * ## Three states, not two
 *
 * The obvious control is a light/dark switch. It is wrong, and the reason is
 * the same one that shaped the token contract this pairs with: the theme has
 * **three** states, not two.
 *
 *   - `system` — no `data-theme` attribute at all, so `prefers-color-scheme`
 *     governs. This is the default, and the state most visitors are in.
 *   - `light` / `dark` — an explicit choice, stamped on the root element, which
 *     beats the OS in both directions.
 *
 * A binary toggle has to stamp an attribute to do anything, so the first tap
 * moves the viewer out of `system` permanently. Someone who flips to dark to
 * read at night is then stuck in dark all summer, with no control that says
 * "go back to following my phone". That is a one-way door built by accident.
 *
 * So `system` is a first-class option here, and choosing it REMOVES the
 * attribute rather than computing a value and stamping it. Removing is what
 * keeps the choice live: the viewer's OS can change at sunset and the app
 * follows, which is the whole point of picking it.
 *
 * ## Why `defaultChoice` is a prop
 *
 * `apps/marketing` is dark-first — the cinematic home rebuild is designed
 * around it, it is live on kilatcarwash.net, and turning it into a
 * system-follower would change the flagship page's appearance for everyone on
 * a light OS. That is a product decision, not a bug fix. So marketing passes
 * `defaultChoice="dark"` and behaves exactly as it does today, while `find` and
 * `dashboard` pass `system` and follow the viewer.
 *
 * ## The boot script stays in each index.html
 *
 * Preventing a flash of the wrong theme means stamping the attribute BEFORE
 * first paint, which means an inline script in `index.html` — static HTML
 * cannot import from this package. Each app therefore carries its own small
 * copy. {@link THEME_STORAGE_KEY} is the contract between them; keep the two
 * in step, and note that a boot script must not stamp anything at all when the
 * stored value is `system`.
 */

/** What the viewer picked. */
export type ThemeChoice = 'system' | 'light' | 'dark';

/** What that resolves to right now, once the OS has had its say. */
export type ResolvedTheme = 'light' | 'dark';

/**
 * The localStorage key every app shares.
 *
 * Shared deliberately: the apps sit on sibling hostnames in production but on
 * `localhost` in development, where they DO share an origin. A per-app key
 * would make a developer's theme choice silently different in each tab and
 * turn "does dark mode work" into a question nobody can answer quickly.
 */
export const THEME_STORAGE_KEY = 'kilat-theme';

/**
 * Keys the two pre-existing implementations wrote, read once so nobody loses a
 * choice they already made.
 *
 * `apps/marketing` wrote `kilat-theme`; `apps/find` wrote `kilat.theme`. The
 * hyphen/dot split is exactly the drift that comes of two apps solving the same
 * problem separately, and it is invisible until you consolidate them. Dropping
 * the old keys would silently reset every existing viewer to the default, which
 * is a small betrayal of a setting someone deliberately chose.
 *
 * Read-only and in priority order. Nothing writes these again — the first
 * explicit choice after this ships migrates the viewer to the shared key.
 */
const LEGACY_KEYS: readonly string[] = ['kilat-theme', 'kilat.theme'];

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isChoice(v: unknown): v is ThemeChoice {
  return v === 'system' || v === 'light' || v === 'dark';
}

/** What the OS currently asks for. Assumes light where it cannot be asked. */
function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/**
 * Read the stored choice.
 *
 * Falls back to `defaultChoice` for anything unrecognised — including the
 * legacy values marketing has been writing, which are bare `light`/`dark` and
 * happen to still be valid here. A value this function does not recognise must
 * never throw or blank the app; a wrong theme is recoverable in one tap.
 */
function readStoredChoice(storageKey: string, defaultChoice: ThemeChoice): ThemeChoice {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (isChoice(raw)) return raw;
    for (const legacy of LEGACY_KEYS) {
      if (legacy === storageKey) continue;
      const old = window.localStorage.getItem(legacy);
      if (isChoice(old)) return old;
    }
    return defaultChoice;
  } catch {
    // Storage blocked (private mode, or disabled cookies). The in-memory
    // choice still works for this session, which is the graceful outcome.
    return defaultChoice;
  }
}

/**
 * Put the choice on the document.
 *
 * `system` REMOVES the attribute. Stamping a computed `light`/`dark` instead
 * would look identical on the first render and then quietly stop following the
 * OS, which is the bug this whole three-state design exists to avoid.
 */
function applyChoice(choice: ThemeChoice): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  if (choice === 'system') el.removeAttribute('data-theme');
  else el.setAttribute('data-theme', choice);
}

interface ThemeApi {
  /** What the viewer picked, including `system`. */
  readonly choice: ThemeChoice;
  /** What that resolves to right now — what is actually on screen. */
  readonly resolved: ResolvedTheme;
  setChoice(next: ThemeChoice): void;
  /** Advance system → light → dark → system. */
  cycle(): void;
}

const ThemeContext = createContext<ThemeApi | null>(null);

export interface ThemeProviderProps {
  readonly children: ReactNode;
  /**
   * Where an app starts before the viewer has chosen. `system` for anything
   * that should follow the device; `dark` for a deliberately dark-first design.
   */
  readonly defaultChoice?: ThemeChoice;
  readonly storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultChoice = 'system',
  storageKey = THEME_STORAGE_KEY,
}: ThemeProviderProps) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() =>
    typeof window === 'undefined' ? defaultChoice : readStoredChoice(storageKey, defaultChoice),
  );
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  // The boot script stamps before paint, but it cannot know about a
  // `defaultChoice` this provider was given, and it never runs at all in a
  // test renderer. Re-applying on mount makes the DOM agree with state in
  // every environment rather than only the one the script covers.
  const applied = useRef(false);
  if (!applied.current && typeof document !== 'undefined') {
    applied.current = true;
    applyChoice(choice);
  }

  const setChoice = useCallback(
    (next: ThemeChoice) => {
      setChoiceState(next);
      applyChoice(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        /* storage blocked — the in-memory choice still holds for this session */
      }
    },
    [storageKey],
  );

  const cycle = useCallback(() => {
    setChoice(choice === 'system' ? 'light' : choice === 'light' ? 'dark' : 'system');
  }, [choice, setChoice]);

  // Follow the OS while the viewer is on `system`. Subscribed unconditionally
  // rather than only when `choice === 'system'` so that switching back to
  // system picks up a change made while an explicit choice was active.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Mirror a choice made in another tab, so a toggle never shows a state the
  // document is not actually in.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey || !isChoice(e.newValue)) return;
      setChoiceState(e.newValue);
      applyChoice(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  const value = useMemo<ThemeApi>(
    () => ({
      choice,
      resolved: choice === 'system' ? system : choice,
      setChoice,
      cycle,
    }),
    [choice, system, setChoice, cycle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Read the theme.
 *
 * Returns an inert value outside a provider rather than throwing. A missing
 * provider must degrade to "the OS decides and the control does nothing", not
 * to a blank screen — the theme is chrome, and chrome must never be able to
 * take the page down with it.
 */
export function useTheme(): ThemeApi {
  const v = useContext(ThemeContext);
  const [system] = useState<ResolvedTheme>(systemTheme);
  return (
    v ?? {
      choice: 'system',
      resolved: system,
      setChoice: () => undefined,
      cycle: () => undefined,
    }
  );
}

export interface ThemeToggleProps {
  /**
   * Localized copy. Supplied by the caller so this component stays independent
   * of any one app's `useI18n`, exactly as `LocaleSwitcher` does.
   */
  readonly labels: {
    /** Accessible name for the control itself, e.g. "Appearance". */
    readonly group: string;
    readonly system: string;
    readonly light: string;
    readonly dark: string;
  };
  /** Compact drops the text label, for a crowded header. */
  readonly compact?: boolean;
}

const ORDER: readonly ThemeChoice[] = ['system', 'light', 'dark'];

/**
 * A three-segment appearance control.
 *
 * A segmented control rather than a cycling icon button: with three states, a
 * cycling button gives no clue what the next tap does or which state you are
 * in — you learn by pressing it and watching the page change, which is a bad
 * trade for a control people use once. All three options are visible and
 * one tap away, and the selected one is marked for screen readers as well as
 * visually, so the state is never carried by colour alone.
 */
export function ThemeToggle({ labels, compact = false }: ThemeToggleProps) {
  const { choice, setChoice } = useTheme();

  const iconFor = (c: ThemeChoice) =>
    c === 'system' ? (
      <ThemeAutoIcon size={14} />
    ) : c === 'light' ? (
      <SunIcon size={14} />
    ) : (
      <MoonIcon size={14} />
    );

  const labelFor = (c: ThemeChoice) =>
    c === 'system' ? labels.system : c === 'light' ? labels.light : labels.dark;

  return (
    <div
      role="radiogroup"
      aria-label={labels.group}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: 2,
        borderRadius: 999,
        border: '1px solid var(--color-border)',
        background: 'var(--tint-subtle)',
      }}
    >
      {ORDER.map((c) => {
        const selected = choice === c;
        return (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={compact ? labelFor(c) : undefined}
            title={labelFor(c)}
            onClick={() => {
              // No `if (selected) return` guard. It read `selected` from the
              // render closure, so two taps landing before React re-rendered
              // saw stale state and the second one silently did nothing.
              // Setting the choice that is already set is idempotent, so the
              // guard bought nothing and cost correctness.
              feel('tap');
              setChoice(c);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              // `em` so the control grows with the viewer's font scale rather
              // than clipping its own label at 150% (responsive contract).
              padding: compact ? '0.45em 0.55em' : '0.4em 0.75em',
              // Both axes. The height was already on the token; the width was
              // left to the icon plus padding, which measured 28px in compact
              // mode — a tap target narrower than the contract, and the
              // narrowest thing in either header it sits in.
              minHeight: 'var(--touch-target, 2.75rem)',
              minWidth: compact ? 'var(--touch-target, 2.75rem)' : undefined,
              justifyContent: 'center',
              borderRadius: 999,
              border: 'none',
              cursor: selected ? 'default' : 'pointer',
              background: selected ? 'var(--color-surface)' : 'transparent',
              boxShadow: selected ? 'var(--shadow-1)' : 'none',
              // Selected is carried by THREE cues, not one: a raised surface,
              // a weight change, and the brand colour. On a dark ground the
              // surface shift alone was almost invisible — and colour alone
              // would fail anyone who cannot see it. aria-checked carries it
              // for assistive tech independently of all three.
              color: selected ? 'var(--color-primary)' : 'var(--color-on-surface-muted)',
              font: 'inherit',
              fontSize: '0.8125rem',
              fontWeight: selected ? 600 : 500,
              lineHeight: 1,
              // filter is listed because transition is not additive: this inline
              // declaration replaces the one premium-motion.css puts on every
              // button, so without it this control alone would snap on hover
              // while the rest of the product eased.
              transition:
                'background 160ms var(--ease-snappy, ease), color 160ms ease, filter 120ms ease',
            }}
          >
            {iconFor(c)}
            {!compact && <span>{labelFor(c)}</span>}
          </button>
        );
      })}
    </div>
  );
}
